/* eslint-disable */
import {CircularProgress, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { useRef } from "react";
import { connect, useSelector, type ConnectedProps } from "react-redux";
import AuthError from "../../../../components/ErrorComponents/AuthError";
import GenericModal from "../../../../components/Modal";
import LanguageStrings from "../../../../i18n/locales";
import { type RootState } from "../../../../store";
import { gatewayDeleteCampaign, fetchGatewayList } from "../../redux/actionCreators";
import { GatewayListState } from "../../redux/gatewayListSlice";
import { APIConfiguration } from "../../../../utils/apiConfiguration";
import ViewGateway from "../ViewGateway/ViewGateway";
import { useNavigate } from "react-router-dom";
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator';
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'

const GatewayManagementStrings = LanguageStrings().GatewayManagementCollectionStrings.TableComponent;
const httpStatus = APIConfiguration;

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  name: string;
  description: string;
  ip_address: string;
  port: string;
  communication_status: string;
  device_type_id: string;
}

export type Order = "asc" | "desc";
interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}
const headCells: readonly HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.GatewayName,
    sortable: true,
  },
  {
    id: "device_type_id",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.GatewayType,
    sortable: true,
  },
  {
    id: "ip",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.GatewayIP,
    sortable: true,
  },
  {
    id: "port",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.PortNumber,
    sortable: true,
  },
  {
    id: "communication_status",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.CommunicationStatus,
    sortable: true,
  },
  {
    id: "communication_protocol",
    numeric: false,
    disablePadding: false,
    label: 'Communication Protocol',
    sortable: false,
  },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.GatewayDescription,
    sortable: false,
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: GatewayManagementStrings.Action,
    sortable: false,
  },
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort, gatewayList } = props;
  const createSortHandler =
  (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, newOrderBy);
  };

  // istanbul ignore else
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: any) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              sx={{
                "& .MuiTableSortLabel-icon": {
                  opacity: 1
                }
              }}
              hideSortIcon={!headCell.sortable}
              direction={orderBy === headCell.id ? order : "desc"}
              onClick={
                headCell.sortable ? createSortHandler(headCell.id) : undefined
              }
              aria-label={headCell.label}
              data-testid="test-sort"
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

export interface Sort {
  order: Order;
  orderBy: string;
}

function GatewayList(props: AllProps) {
  const navigate = useNavigate();
  const [confirmDeletePopup, setConfirmDeletePopup] = React.useState(false);
  const [selectedGatewayId, setSelectedGatewayId] = React.useState("");
  const [selectedGatewayName, setSelectedGatewayName] = React.useState("");
  const [showDeleteStatus, setShowDeleteStatus] = React.useState(false);
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  
  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  const gatewayDeleteStatus = useSelector(
    (state: RootState) => state.gatewayDeleteState
  );
  const [open, setOpen] = React.useState<any>("");
  // const [getGatewayTitle, setGatewayTitle] = React.useState("");
  // const [getAssetTypeId, setAssetTypeId] = React.useState("");

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  // istanbul ignore next
  function useOutsideAlerter(ref: any) {}

  React.useEffect(() => {
    // istanbul ignore else
    if (props.searchVal === "") {
      fetchAllAssetList();
    } else if (props.searchVal !== "") {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllAssetList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchGatewayList(
        props.parentSiteId,
        props.assetTypeId,
        props.page,
        props.rowsPerPage,
        props.searchVal,
        props.sort.order,
        props.parentAssetId,
        props.sort.orderBy
      );
    }
  };

  // istanbul ignore next
  const handleRequestSort = ( event: React.MouseEvent<unknown>, newOrderBy: keyof Data ) => {
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    const toggledOrder = isAsc ? "desc" : "asc";
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  const ViewGatewayOpen = (id: any): any => {
    setOpen(id);
  };

  // istanbul ignore next
  const handleGatewayDetailClick = (assetId: string, assetName: string) => {
    navigate(`/device-list/${assetId}`, { state: { parentSiteId: props.parentSiteId, deviceAssetId: props.parentAssetId } });
  };

  const formatNameWithTooltip = (text: string): JSX.Element => {
    if (text?.length > 20) {
      return (<Tooltip title={text}><span>{`${text.substring(0, 20)}...`}</span></Tooltip>)
    }
    return <span>{text !== '' ? text : '-'}</span>
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "& th": {
      border: "1px solid #202020",
    },
  }));

  // istanbul ignore next
  const handleDeleteCampaignWithFailure = (): JSX.Element => {
    if (gatewayDeleteStatus.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={GatewayManagementStrings.successfullyDeleted}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false), fetchAllAssetList();
          }}
          onClose={() => {
            setShowDeleteStatus(false), fetchAllAssetList();
          }}
        >
          <p style={{ paddingTop: "1.2em" }}>
            {" "}
            {GatewayManagementStrings.FullMsgGatewayDeletedSuccessfully}{" "}
          </p>
        </GenericModal>
      );
    } else if (gatewayDeleteStatus.isLoading) {
      return (
        <GenericModal title={"Loading..."}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </GenericModal>
      );
    } else if (gatewayDeleteStatus.httpStatus === httpStatus.BAD_REQUEST) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {gatewayDeleteStatus.error} </p>
        </GenericModal>
      );
    } else if (
      gatewayDeleteStatus.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <div className="authError">
          <AuthError errorMessage="Unauthorized" />
        </div>
      );
    } else if (
      gatewayDeleteStatus.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <div className="authError">
          <AuthError errorMessage="accessForbidden" />
        </div>
      );
    } else {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {gatewayDeleteStatus.error} </p>
        </GenericModal>
      );
    }
  };

  // istanbul ignore else
  if (
    props.gatewayList.gatewayList !== null &&
    props.gatewayList.gatewayList !== undefined &&
    props.gatewayList.httpStatus === httpStatus.SUCCESS_HTTP_STATUS &&
    props.gatewayList.gatewayCount > 0
  ) {
    return (
      <>
        <Box className="gateways">
          <Paper
            sx={{
              width: "100%",
              mb: 2,
              backgroundColor: "#272727",
              boxShadow: "unset",
              color: "#F0F0F0",
              marginBottom: "0",
            }}
          >
            <TableContainer className="gateway-table">
              <Table sx={{ minWidth: 750, color: "#f0f0f0" }} aria-labelledby="tableTitle" size={"small"}>
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.gatewayList.gatewayList.length}
                  gatewayList={props.gatewayList.gatewayList}
                  gatewayListCount={props.gatewayList.gatewayList.length}
                />
                <TableBody>
                  {props.gatewayList.gatewayList &&
                    props.gatewayList.gatewayList.map(
                      (row: any, index: any) => {
                        const labelId = `enhanced-table-checkbox-${index}`;
                        return (
                          <StyledTableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={row.gatewayId}
                            aria-label="gateways-list-row"
                            sx={{ cursor: "pointer" }}
                          >
                            <StyledTableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                              onClick={() =>
                                handleGatewayDetailClick(row.id, row.name)
                              }
                              aria-label="gateway-name"
                              data-testid="gateway-name"
                              sx={{ color: "#64C3FF !important", width: '15%' }}
                              className="column-ellipsis"
                            >
                              {formatNameWithTooltip(row?.name)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.gateway_type == "Thermal-Gateway-TransparentModem" ? "Transparent Gateway" : "Smart Gateway"}
                            </StyledTableCell>
                            <StyledTableCell>{row.ip_address}</StyledTableCell>
                            <StyledTableCell>{row.port !== '' ? row.port : '-'}</StyledTableCell>
                            <StyledTableCell sx={{ width: '15%' }}>
                              <span style={{ color: row.communication_status === "Online" ? "green" : "orangered" }}>
                                {row.communication_status ? row.communication_status : "Offline"}
                              </span>
                            </StyledTableCell>
                            <StyledTableCell>{row.communication_protocol}</StyledTableCell>
                            <StyledTableCell sx={{ width: '15%' }} className="column-ellipsis">{formatNameWithTooltip(row.description)}</StyledTableCell>
                            <StyledTableCell>
                              {operationPermissions.permissions.can_read_tiq_assets && (
                                <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                  onClick={() => ViewGatewayOpen(row.id)} data-testid={'gateway-management-view_' + String(index)}
                                >
                                  {GatewayManagementStrings.View}
                                </span>
                              )}
                              {operationPermissions.permissions.can_modify_tiq_assets && (
                                <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                  onClick={() => {
                                    navigate(`/edit-gateway/${row.id}`, { state: { parentSiteId: props.parentSiteId, deviceAssetId: props.parentAssetId } }) 
                                  }}
                                  data-testid={"gateway-management-edit_" + String(index)}
                                >
                                  {GatewayManagementStrings.Edit}
                                </span>
                              )}
                              {operationPermissions.permissions.can_delete_tiq_assets && (
                                <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px" }}
                                  onClick={() => {
                                    setSelectedGatewayId(row.id);
                                    setSelectedGatewayName(row.name);
                                    setConfirmDeletePopup(true);
                                  }}
                                  data-testid={'gateway-management-delete_' + String(index)}
                                >
                                  {GatewayManagementStrings.Delete}
                                </span>
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      }
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
        {open !== "" && (
          <>
            <div className="overlay"></div>
            <ViewGateway
              viewGatewayPopup={(params?: any) => {
                setOpen(params);
              }}
              data-testid="viewgateway_chk"
              gatewayId={open}
              assetId={props.parentAssetId}
            />
          </>
        )}
        {confirmDeletePopup && (
          <GenericModal
            buttonTypeNegative="hollow"
            title={`${GatewayManagementStrings.Delete} ${selectedGatewayName}`}
            LabelPositive="Confirm"
            LabelNegative="Cancel"
            onClickPositive={() => {
              setConfirmDeletePopup(false);
              setShowDeleteStatus(true);
              props.gatewayDeleteCampaign(selectedGatewayId);
            }}
            onClose={() => {
              setConfirmDeletePopup(false);
            }}
            onClickNegative={() => {
              setConfirmDeletePopup(false);
            }}
          >
            <p style={{ paddingTop: "1.2em", fontSize: "12px" }}>
              {GatewayManagementStrings.DeleteConfirmation}
              <br />
              {GatewayManagementStrings.DeleteConfirmationWarning}
            </p>
          </GenericModal>
        )}
        {showDeleteStatus && handleDeleteCampaignWithFailure()}
      </>
    );
  } else if (props.gatewayList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (
    props.gatewayList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    );
  } else if (
    props.gatewayList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    );
  } else if (
    props.gatewayList.gatewayList !== null &&
    props.gatewayList.gatewayList !== undefined &&
    props.gatewayList.gatewayList.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else if (
    props.gatewayList.loadingError?.includes(
      GatewayManagementStrings.NoAssetsFound
    ) ||
    props.gatewayList.loadingError?.includes(
      GatewayManagementStrings.AssetNotFound
    )
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError errorMessage="cannotFetch" retry={fetchAllAssetList} />
      </div>
    );
  }
}

interface DispatchToProps {
  fetchGatewayList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void;
  gatewayDeleteCampaign: (selectedGatewayId: string) => void;
  getOperationPermissions: (sub: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchGatewayList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) =>
    dispatch(
      fetchGatewayList(
        parentSiteId,
        assetTypeId,
        PageNumber,
        PageItems,
        searchKey,
        SortParams,
        parentAssetId,
        sortField
      )
    ),
  gatewayDeleteCampaign: (selectedGatewayId: string) =>
    dispatch(gatewayDeleteCampaign(selectedGatewayId)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
});

interface StateToProps {
  gatewayList: GatewayListState;
}
const mapStateToProps = (state: RootState): StateToProps => ({
  gatewayList: state.gatewayManagementGatewayList,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface ParentProps {
  parentSiteId: string;
  assetTypeId: string;
  parentAssetId: string;
  searchVal: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}

type AllProps = PropsFromRedux & ParentProps;

export default connector(GatewayList);